import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoMark from "@/components/LogoMark";
import { useAuth } from "@/context/AuthContext";
import { friendlyAuthError, getSession, getUser } from "@/lib/auth";

export default function AuthCallback() {
  const { consumeAuthCallback } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const [existingUser, setExistingUser] = useState(null);
  const callbackAttempt = useRef(null);

  useEffect(() => {
    let live = true;
    let timer;
    // Reuse this attempt across effect replays; never consume the URL twice.
    if (!callbackAttempt.current) {
      callbackAttempt.current = (async () => {
        const nativeError = sessionStorage.getItem("un_auth_callback_error");
        if (nativeError) {
          sessionStorage.removeItem("un_auth_callback_error");
          const err = new Error(nativeError);
          err.nativeCallbackError = true;
          throw err;
        }
        return consumeAuthCallback();
      })();
    }
    callbackAttempt.current
      .then(() => {
        if (live) timer = setTimeout(() => nav("/home", { replace: true }), 350);
      })
      .catch(async (err) => {
        // A reused link can fail while an earlier sign-in remains valid.
        // Validate that existing session with the server; do not accept the
        // failed link or treat a cached user as proof of authentication.
        let verifiedUser = null;
        try {
          const existing = await getSession();
          if (existing?.access_token) verifiedUser = await getUser(existing.access_token);
        } catch { /* Preserve the genuine callback error when validation fails. */ }
        if (live) {
          setExistingUser(verifiedUser?.id ? verifiedUser : null);
          setError(err.nativeCallbackError ? err.message : friendlyAuthError(err));
        }
      });
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [consumeAuthCallback, nav]);

  return <div className="min-h-screen grid place-items-center px-5"><div className="text-center max-w-[390px]"><LogoMark size={48} className="mx-auto" title=""/><h1 className="mt-4 text-[28px]" style={{ fontFamily: "Lora, serif", color: "var(--un-plum-deep)" }}>{existingUser ? "You’re already signed in." : error ? "That link didn’t work." : "Opening your private space…"}</h1><p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>{existingUser ? "This link could not be used. Your existing sign-in is still active." : error || "Your account session is being secured on this device."}</p>{existingUser ? <button onClick={() => nav("/home", { replace: true })} className="mt-5 px-5 py-3 rounded-full text-[13px]" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>Open UnAnonymous</button> : error && <button onClick={() => nav("/auth?mode=signin")} className="mt-5 px-5 py-3 rounded-full text-[13px]" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>Request a new sign-in link</button>}</div></div>;
}
